import {useCallback, useState, useRef} from 'react'
import dayjs from 'dayjs'
import {AgendaEventAppointment, AgendaEventType} from '@/features/agenda/types'
import {EventInteractionArgs} from 'react-big-calendar/lib/addons/dragAndDrop'
import {useAgendaCalendarStore} from '@/features/agenda/stores/agendaCalendarStore'
import {useShallow} from 'zustand/react/shallow'
import {hasOverlap} from '@/features/agenda/utils'
import {useEditEvent} from '@/features/agenda/queries/useEditEvent'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {SlotInfo} from 'react-big-calendar'
import {CreateEventFormValidationSchema} from '@/features/agenda/formSchemas'
import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'
import {useAuthStore} from '@/features/auth/store/store'
import {DEFAULT_START_TIME} from '@/features/users-availability/utils'
import {createDateTime, formatDate, formatDisplayTime} from '@/dayjs'
import {useGetUser} from '@/features/users/queries/useGetUser'
import {ResponseType} from '@/types'
import {useAgendaSubjects} from '@/features/agenda/hooks/useAgendaSubjects'
import {PatientSmall} from '@/features/patients/types'

interface UseEventHandlersProps {
    blockouts: AgendaEventType[]
    validateEventMove: (event: EventInteractionArgs<AgendaEventType>) => boolean
    validateEventResize: (event: EventInteractionArgs<AgendaEventType>) => boolean
    validateSlotSelection: (slotInfo: {start: Date; end: Date}) => boolean
}

export const useEventHandlers = ({
    blockouts,
    validateEventMove,
    validateEventResize,
    validateSlotSelection
}: UseEventHandlersProps) => {
    const [selectedCalendarSlot, setSelectedCalendarSlot] = useState<Partial<CreateEventFormValidationSchema> | null>(
        null
    )

    // Add ref to track click timeout for double-click detection
    const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const authUser = useAuthStore(useShallow(state => state.user))
    const currentUser = useGetUser({
        userId: authUser?.id,
        responseType: ResponseType.enum.small,
        options: {enabled: !!authUser?.id}
    })

    const {params, navigateWithParams} = useAgendaNavigation()

    const {entity, view} = params

    const {subjects, patient} = useAgendaSubjects<NonNullable<typeof entity>>()

    // Get the moveEvent and resizeEvent functions from the store
    const {moveEvent: moveEventStore, resizeEvent: resizeEventStore} = useAgendaCalendarStore(
        useShallow(state => ({
            moveEvent: state.moveEvent,
            resizeEvent: state.resizeEvent
        }))
    )

    // Use the edit event mutation
    const {mutate: updateEvent, isPending: isUpdatingEvent} = useEditEvent({
        onSuccess: async (_, {urlParams}) => {
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS, 'extended', urlParams.eventId]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS, 'extended', urlParams.eventId]})
        },
        onError: error => {
            // In case of error, cancel the query and invalidate the cache
            console.error('Error during event update: ', error)
            void queryClient.cancelQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
        }
    })

    /**
     * Handles double-click on a slot to navigate to different views
     */
    const handleDoubleClickSlot = useCallback(
        (start: Date) => {
            // Clear any pending single-click timeout
            if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current)
                clickTimeoutRef.current = null
            }

            // Navigate to week view if current view is month or day
            // Navigate to day view if current view is week
            setSelectedCalendarSlot(null)
            if (view === 'month' || view === 'day') {
                navigateWithParams({...params, view: 'week', date: formatDate(start, 'DD-MM-YYYY')})
            } else if (view === 'week') {
                navigateWithParams({...params, view: 'day', date: formatDate(start, 'DD-MM-YYYY')})
            }
        },
        [view, navigateWithParams, params]
    )

    /**
     * Prepares event update data
     * @param event The event to prepare update data for
     * @returns The update data or null if validation fails
     */
    const prepareEventUpdate = useCallback(
        (event: EventInteractionArgs<AgendaEventAppointment>) => {
            // Check if the event can be moved or resized based on disabled days
            if (!validateEventMove(event)) {
                console.log('Invalid event move')
                return null
            }

            // Check for overlaps with blockouts
            if (
                hasOverlap({start: event.start, end: event.end, blockouts, resourceId: event?.resourceId?.toString()})
            ) {
                return null
            }

            return {
                urlParams: {eventId: event.event.id},
                payload: {
                    eventConsultants: event.event.eventConsultants?.map(consultant => ({
                        userId: consultant.userId,
                        accessTypeId: consultant.accessTypeId,
                        specializationId: consultant.user.specializationId,
                        specializationName: consultant.user.specialization
                    })),
                    eventPatients: event.event.eventPatients?.map(patient => ({
                        patientId: patient.patientId,
                        projectId: patient.projectId
                    })),
                    eventTeamMembers: event.event.eventTeams?.map(teamMember => ({
                        treatmentRequestTeamId: teamMember.treatmentRequestTeamId
                    })),
                    date: formatDate(event.start),
                    time: formatDisplayTime(event.start),
                    duration: dayjs(event.end).diff(dayjs(event.start), 'minute')
                }
            }
        },
        [blockouts, validateEventMove]
    )

    /**
     * Handles moving an event
     * @param event The event to move
     */
    const handleMoveEvent = useCallback(
        (event: EventInteractionArgs<AgendaEventAppointment>) => {
            const updateData = prepareEventUpdate(event)
            if (
                !updateData ||
                event.event.status == 'missed' ||
                event.event.status == 'completed' ||
                event.event.status == 'withdrawn'
                // Todo: add project suspension check
            ) {
                return
            }

            // Update the store first for immediate UI feedback
            moveEventStore(event)
            // Then update the backend
            updateEvent(updateData)
        },
        [moveEventStore, prepareEventUpdate, updateEvent]
    )

    /**
     * Handles resizing an event
     * @param event The event to resize
     */
    const handleResizeEvent = useCallback(
        (event: EventInteractionArgs<AgendaEventAppointment>) => {
            // Additional validation specific to resize
            if (
                !validateEventResize(event) ||
                event.event.status == 'missed' ||
                event.event.status == 'completed' ||
                event.event.status == 'withdrawn'
                // Todo: add project suspension check
            ) {
                return
            }

            const updateData = prepareEventUpdate(event)
            if (!updateData) return

            // Update the store first for immediate UI feedback
            resizeEventStore(event)
            // Then update the backend
            updateEvent(updateData)
        },
        [prepareEventUpdate, resizeEventStore, updateEvent, validateEventResize]
    )

    /**
     * Handles selecting a slot in the calendar
     * @param slot The selected slot
     */
    const handleSelectSlot = useCallback(
        (slot: SlotInfo) => {
            if (slot.action == 'doubleClick') {
                handleDoubleClickSlot(slot.start)
            } else {
                // Clear any existing timeout
                if (clickTimeoutRef.current) {
                    clearTimeout(clickTimeoutRef.current)
                }

                // Set a timeout to delay the single-click action
                // This allows time for a potential double-click to be detected
                clickTimeoutRef.current = setTimeout(() => {
                    const defaultStart = parseInt(DEFAULT_START_TIME.split(':')[0])
                    const start = parseInt(dayjs(slot.start).format('HH'))

                    // Then validate selection
                    if (!validateSlotSelection(slot)) {
                        console.log('Invalid slot')
                        return
                    }

                    // Check if the slot overlaps with any background event (blockouts)
                    if (
                        hasOverlap({
                            start: slot.start,
                            end: slot.end,
                            blockouts,
                            resourceId: slot.resourceId?.toString() || undefined
                        })
                    ) {
                        return
                    }

                    const transformSubjectToConsultant = (subject: {
                        id: string
                        fullName: string
                        specializationId?: string
                        specializationName?: string
                    }) => ({
                        id: subject.id,
                        fullName: subject.fullName,
                        specializationId: subject.specializationId,
                        specializationName: subject.specializationName
                    })

                    const getSelectedConsultants = () => {
                        if (!subjects?.length || entity !== 'users') {
                            return currentUser.data
                                ? [transformSubjectToConsultant(currentUser.data)]
                                : [{id: '', fullName: ''}]
                        }

                        if (subjects.length > 1) {
                            const selectedSubject = subjects.find(subject => subject.id === slot.resourceId)
                            return selectedSubject ? [transformSubjectToConsultant(selectedSubject)] : []
                        }

                        return subjects.map(transformSubjectToConsultant)
                    }

                    const consultants = getSelectedConsultants()

                    const patients =
                        entity == 'patients' && subjects?.length
                            ? (subjects as PatientSmall[]).map(subject => ({
                                  id: subject.id,
                                  fullName: subject.fullName,
                                  treatmentRequestId: subject.treatmentRequestId || null,
                                  neuromuscularDiagnosisId: subject.neuromuscularDiagnosisId || null,
                                  project: subject.project && !subject.project.isSuspended ? subject.project : null
                              }))
                            : []

                    // Additional slot selection logic would go here
                    setSelectedCalendarSlot({
                        type: 'event',
                        category: 'treatments',
                        status: 'provisional',
                        isGroupTreatment: false,
                        isRemote: false,
                        duration: '60',
                        consultants,
                        patients: entity == 'users' && patient ? [patient] : patients,
                        date: {
                            date: start < defaultStart ? createDateTime(slot.start, DEFAULT_START_TIME) : slot.start,
                            time: start < defaultStart ? DEFAULT_START_TIME : dayjs(slot.start).format('HH:mm'),
                            location: 'clinic'
                        }
                    })
                }, 300) // 300ms delay to allow for double-click detection
            }
        },
        [validateSlotSelection, blockouts, entity, subjects, authUser, handleDoubleClickSlot]
    )

    return {
        handleMoveEvent,
        handleResizeEvent,
        handleSelectSlot,
        isUpdatingEvent,
        selectedCalendarSlot,
        setSelectedCalendarSlot
    }
}
